import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { updateUserProfile } from '../../firebase/firestore';
import { Button, Checkbox } from '../ui';

/**
 * NotificationsTab - Notification preferences
 *
 * Allows users to control which email notifications they receive.
 * Preferences are saved to Firestore and checked before sending emails.
 */
export default function NotificationsTab() {
  const { user, userProfile } = useAuth();
  const { showToast } = useToast();
  const [saving, setSaving] = useState(false);

  // Default preferences (will come from userProfile.notificationPreferences)
  const [preferences, setPreferences] = useState({
    emailEntryConfirmed: userProfile?.notificationPreferences?.emailEntryConfirmed ?? true,
    emailWinnerAnnounced: userProfile?.notificationPreferences?.emailWinnerAnnounced ?? true,
    emailNonWinner: userProfile?.notificationPreferences?.emailNonWinner ?? true,
    emailCancelled: userProfile?.notificationPreferences?.emailCancelled ?? true,
    emailHandoverReminder: userProfile?.notificationPreferences?.emailHandoverReminder ?? true,
    emailPayoutSent: userProfile?.notificationPreferences?.emailPayoutSent ?? true,
    marketingEmails: userProfile?.notificationPreferences?.marketingEmails ?? false,
  });

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUserProfile(user.uid, {
        notificationPreferences: preferences,
      });
      showToast({ title: 'Preferences saved', type: 'success' });
    } catch (error) {
      console.error('Error saving preferences:', error);
      showToast({ title: 'Save failed', description: error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const notificationGroups = [
    {
      title: 'Competition Updates',
      description: 'Notifications about competitions you\'ve entered',
      items: [
        {
          key: 'emailEntryConfirmed',
          label: 'Entry Confirmation',
          description: 'When your competition entry is confirmed',
        },
        {
          key: 'emailWinnerAnnounced',
          label: 'Winner Announcement',
          description: 'When you win a competition',
        },
        {
          key: 'emailNonWinner',
          label: 'Draw Results',
          description: 'When a competition you entered ends (non-winner)',
        },
        {
          key: 'emailCancelled',
          label: 'Cancellation & Refunds',
          description: 'When a competition is cancelled and you receive a refund',
        },
      ],
    },
    {
      title: 'Seller Notifications',
      description: 'Notifications for sellers',
      items: [
        {
          key: 'emailHandoverReminder',
          label: 'Handover Reminders',
          description: 'Reminders about pending handovers',
        },
        {
          key: 'emailPayoutSent',
          label: 'Payout Notifications',
          description: 'When funds are sent to your bank',
        },
      ],
    },
    {
      title: 'Marketing',
      description: 'Promotional content and updates',
      items: [
        {
          key: 'marketingEmails',
          label: 'Marketing Emails',
          description: 'News, tips, and special offers from Raflr',
        },
      ],
    },
  ];

  return (
    <div className="space-y-8">
      {/* Info Banner */}
      <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <InfoIcon className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div>
            <p className="text-cream font-medium">Email Notification Preferences</p>
            <p className="text-sm text-cream/60 mt-1">
              Control which emails you receive from Raflr. All transactional emails are sent to your registered email address.
            </p>
          </div>
        </div>
      </div>

      {/* Notification Groups */}
      {notificationGroups.map((group) => (
        <div key={group.title}>
          <h3 className="text-lg font-semibold text-cream mb-2">{group.title}</h3>
          <p className="text-sm text-cream/50 mb-4">{group.description}</p>

          <div className="bg-midnight-lighter/50 rounded-xl border border-cream/5 divide-y divide-cream/5">
            {group.items.map((item) => (
              <div key={item.key} className="flex items-center justify-between p-4">
                <div className="min-w-0 flex-1 mr-4">
                  <p className="text-cream font-medium">{item.label}</p>
                  <p className="text-sm text-cream/50 mt-0.5">{item.description}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences[item.key]}
                    onChange={() => handleToggle(item.key)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-midnight-lighter rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-primary after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-cream after:rounded-full after:h-5 after:w-5 after:transition-all border border-cream/10 peer-checked:border-primary/50"></div>
                </label>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Save Button */}
      <div className="pt-4 border-t border-cream/10">
        <Button onClick={handleSave} loading={saving}>
          Save Preferences
        </Button>
      </div>
    </div>
  );
}

function InfoIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}
